import React from 'react';
import type { ActivityLogEntry, TimerAction } from '../../types';

interface ActivityLogRowProps {
  entry: ActivityLogEntry;
}

const ACTION_CONFIG: Record<
  TimerAction,
  { label: string; icon: string; badgeClass: string }
> = {
  create: {
    label: 'Créé',
    icon: '+',
    badgeClass: 'text-namm-cyan border-namm-cyan/40 bg-namm-cyan/10',
  },
  start: {
    label: 'Démarré',
    icon: '▶',
    badgeClass: 'text-green-400 border-green-500/40 bg-green-500/10',
  },
  pause: {
    label: 'En pause',
    icon: '⏸',
    badgeClass: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
  },
  stop: {
    label: 'Arrêté',
    icon: '⏹',
    badgeClass: 'text-namm-danger border-namm-danger/40 bg-namm-danger/10',
  },
  reset: {
    label: 'Réinitialisé',
    icon: '↺',
    badgeClass: 'text-namm-muted border-namm-line/40 bg-namm-line/10',
  },
  rename: {
    label: 'Renommé',
    icon: '✎',
    badgeClass: 'text-purple-400 border-purple-500/40 bg-purple-500/10',
  },
  delete: {
    label: 'Supprimé',
    icon: '✕',
    badgeClass: 'text-red-500 border-red-500/40 bg-red-500/10',
  },
  game_change: {
    label: 'Jeu',
    icon: '🎮',
    badgeClass: 'text-namm-cyan border-namm-cyan/50 bg-namm-cyan/15 font-semibold',
  },
  toggle_free_play: {
    label: 'Roue Libre',
    icon: '🕊️',
    badgeClass: 'text-violet-400 border-violet-500/40 bg-violet-500/10 font-semibold',
  },
};

export function ActivityLogRow({ entry }: ActivityLogRowProps) {
  if (!entry) return null;

  const timestamp = entry.timestamp || 0;
  const date = timestamp ? new Date(timestamp) : null;
  const timeStr = date && !isNaN(date.getTime())
    ? date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '-';

  const config = (entry.action && ACTION_CONFIG[entry.action]) || {
    label: entry.action || 'Action',
    icon: '•',
    badgeClass: 'text-namm-muted border-namm-line/40',
  };

  const userName = entry.user || 'Anonyme';
  const userInitial = userName.charAt(0).toUpperCase() || '?';

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-namm-panel-soft/50 border border-namm-line/20 font-mono text-xs gap-3">
      {/* Left: user & action */}
      <div className="flex items-center gap-2.5 min-w-0">
        {/* User avatar */}
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-namm-bg shrink-0"
          style={{ backgroundColor: entry.userColor || '#00e5ff' }}
          title={userName}
        >
          {userInitial}
        </div>

        {/* Pseudo */}
        <span className="font-semibold text-namm-text truncate max-w-[100px] sm:max-w-[130px]">
          {userName}
        </span>

        {/* Action badge */}
        <span
          className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-wider shrink-0 flex items-center gap-1 ${config.badgeClass}`}
        >
          <span>{config.icon}</span>
          <span>{config.label}</span>
        </span>

        {/* Timer name */}
        <span className="text-namm-cyan truncate font-medium">
          "{entry.timerName || 'Chronomètre'}"
        </span>

        {/* Details */}
        {entry.details && (
          <span className="text-namm-muted text-[10px] truncate">
            ({entry.details})
          </span>
        )}
      </div>

      {/* Right: timestamp */}
      <span className="text-namm-muted text-[11px] shrink-0 tabular-nums">
        {timeStr}
      </span>
    </div>
  );
}
